import { ROLES } from "@circle-react-shared/RoleSelect";
import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { useAddSpaceGroupMemberModal } from "@circle-react/components/Modals/AddSpaceGroupMember";
import { useRemoveSpaceGroupMemberModal } from "@circle-react/components/Modals/RemoveSpaceGroupMember";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { useShowProfileEdit } from "@circle-react/hooks/profile/useShowProfileEdit";
import { useUpdateSpaceGroupMemberModerator } from "@circle-react/hooks/useUpdateSpaceGroupMemberModerator";
import { useCallback } from "react";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

import type { RawSpaceGroupMember, SpaceGroupMemberRole } from "../helpers";

export const useSpaceGroupMembersTabActions = ({
  queryKey,
  spaceGroupId,
}: {
  queryKey: unknown[];
  spaceGroupId: number;
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const editSpaceGroupModal = useModal();
  const { currentCommunityMember } = usePunditUserContext();
  const { showOwnProfileEdit, showMemberProfileEdit } = useShowProfileEdit();
  const addSpaceGroupMemberModal = useAddSpaceGroupMemberModal();
  const removeSpaceGroupMemberModal = useRemoveSpaceGroupMemberModal();
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  const { mutate: updateSpaceGroupModeratorStatus } =
    useUpdateSpaceGroupMemberModerator({
      onError: (error: { message: string }) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey });
        toast.success(t("space_groups.form.edit.members.changed_role"));
      },
    });

  const handleEditMember = useCallback(
    (member: RawSpaceGroupMember) => {
      const isCurrentMemberProfile =
        String(currentCommunityMember?.id) === String(member.id);

      if (isCurrentMemberProfile) {
        showOwnProfileEdit();
      } else {
        showMemberProfileEdit({
          member: {
            ...member,
            id: Number(member.id),
            public_uid: member.public_uid,
          },
        });
      }

      void editSpaceGroupModal.hide();
    },
    [
      currentCommunityMember?.id,
      editSpaceGroupModal,
      showOwnProfileEdit,
      showMemberProfileEdit,
    ],
  );

  const handleOpenAddMemberModal = useCallback(() => {
    void addSpaceGroupMemberModal.show({ spaceGroupId, queryKey });
  }, [addSpaceGroupMemberModal, queryKey, spaceGroupId]);

  const handleOpenRemoveConfirmation = useCallback(
    (memberId: string) => {
      void removeSpaceGroupMemberModal.show({
        spaceGroupId,
        memberId: Number(memberId),
        queryKey,
      });
    },
    [queryKey, removeSpaceGroupMemberModal, spaceGroupId],
  );

  const handleChangeMemberRole = useCallback(
    (memberId: string, role: SpaceGroupMemberRole) => {
      if (role === ROLES.ADMIN) return;

      updateSpaceGroupModeratorStatus({
        spaceGroupId,
        memberId: Number(memberId),
        isModerator: role === ROLES.MODERATOR,
      });
    },
    [spaceGroupId, updateSpaceGroupModeratorStatus],
  );

  return {
    addMemberButtonLabel: t("space_groups.form.edit.members.add_members"),
    handleChangeMemberRole,
    handleEditMember,
    handleOpenAddMemberModal,
    handleOpenRemoveConfirmation,
    isAdmin,
  };
};
